import React, { Component, Fragment, createRef } from 'react';
import { components, constants } from 'app-webtech-core';
import PropTypes from 'prop-types';
import YouboraClapprPlayerAdapter from 'youbora-adapter-clappr';
import debug from 'debug';
import parse from 'url-parse';
import throttle from 'lodash/throttle';
import youbora from 'youboralib';

import { Captions } from './Captions';

const logger = debug('web:player:clappr');

const HLS_CONFIG = {
  enableWebVTT: true,
  enableCEA708Captions: true,

  startLevel: 0,
  initialLiveManifestSize: 1,

  levelLoadingTimeOut: 15000,
  levelLoadingMaxRetry: 5,
  levelLoadingRetryDelay: 5000,
  levelLoadingMaxRetryTimeout: 64000,

  manifestLoadingTimeOut: 15000,
  manifestLoadingMaxRetry: 3,
  manifestLoadingRetryDelay: 1000,
  manifestLoadingMaxRetryTimeout: 64000,

  fragLoadingTimeOut: 15000,
  fragLoadingMaxRetry: 3,
  fragLoadingRetryDelay: 1000,
  fragLoadingMaxRetryTimeout: 64000,

  startFragPrefetch: true,

  maxBufferLength: 120,
  nudgeMaxRetry: 5,

  appendErrorMaxRetry: 10,
};
const GA_VOD_HEART_BEAT_FIRE_INTERVAL = 20 * 60 * 1000;

class ClapprVideoPlayer extends Component {
  static propTypes = {
    kind: PropTypes.string,
    url: PropTypes.string.isRequired,
    isGooglePalEnabled: PropTypes.bool.isRequired,
    googPalNonce: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    playbackPosition: PropTypes.shape({}).isRequired,
    itemId: PropTypes.string.isRequired,
    isMuted: PropTypes.bool,
    isVod: PropTypes.bool,
    isPerformanceEnabled: PropTypes.bool.isRequired,
    isViral: PropTypes.bool,
    onReady: PropTypes.func.isRequired,
    onPlay: PropTypes.func.isRequired,
    onPause: PropTypes.func.isRequired,
    onBuffer: PropTypes.func.isRequired,
    onComplete: PropTypes.func.isRequired,
    onTime: PropTypes.func.isRequired,
    onDestroy: PropTypes.func.isRequired,
    onAutoPlayBlocked: PropTypes.func.isRequired,
    mutePlayer: PropTypes.func.isRequired,
    unmutePlayer: PropTypes.func.isRequired,

    updateSlug: PropTypes.func.isRequired,

    gaAdImpression: PropTypes.func.isRequired,
    gaHeartBeat: PropTypes.func.isRequired,
    onCaptionList: PropTypes.func.isRequired,
    savePlaybackPosition: PropTypes.func.isRequired,
    setCurrentlyPlayingAdIdAccordingToStream: PropTypes.func.isRequired,
    savePlayerSizes: PropTypes.func.isRequired,
  };

  static defaultProps = {
    kind: null,
    isMuted: false,
    isVod: false,
    isViral: false,
  };

  state = {
    firstPlay: true,
    player: null,
    ready: false,
    tryPlayCount: 0,
  };

  componentDidMount() {
    if (this.props.isPerformanceEnabled) {
      this.youboraPlugin = this.initYouboraPlugin();
    }
    this.props.updateSlug();
    if(!this.props.isGooglePalEnabled) this.initialize();
  }

  shouldComponentUpdate(nextProps, nextState) {
    const urlChange = this.props.url !== nextProps.url;
    const newNonce = this.props.googPalNonce !== nextProps.googPalNonce;
    const playerChange = this.state.player !== nextState.player;
    const performanceChange = this.props.isPerformanceEnabled !== nextProps.isPerformanceEnabled;

    return urlChange || playerChange || performanceChange || newNonce;
  }

  componentDidUpdate(prevProps) {
    if (!this.props.isPerformanceEnabled && prevProps.isPerformanceEnabled) {
      this.youboraPlugin.disable();
      delete this.youboraPlugin;
    }
    if (this.props.isPerformanceEnabled && !prevProps.isPerformanceEnabled) {
      this.youboraPlugin = this.initYouboraPlugin();
    }
    if (this.props.googPalNonce && this.props.googPalNonce !== prevProps.googPalNonce) {
      this.initialize();
    }
    if (this.props.url && this.props.url !== prevProps.url) {
      this.props.updateSlug();
      if (!this.props.isGooglePalEnabled) this.initialize();
    } else if (this.props.isPerformanceEnabled && !prevProps.isPerformanceEnabled) {
      this.initYouboraAdapter(this.state.player);
    }
    if (this.youboraPlugin) {
      this.youboraPlugin.setOptions({
        enabled: true,
        'parse.hls': false,
        'parse.cdnNodeHost': true,
        'parse.cdnNode': false,
        transactionCode: this.props.kind,
        'content.isLive': this.props.kind === constants.player.PLAYER_CONTENT_KIND.LINEAR_CHANNEL,
        'content.title': this.props.title,
        'content.resource': this.props.url,
      });
    }
  }

  componentWillUnmount() {
    this.tryDestroy();
  }

  onHeartBeat = ({ diff, checkHeartBeat }) => {
    const { isVod, gaHeartBeat } = this.props;
    if (isVod) {
      this.gaVODPlaybackTime += diff;
    }

    const timeInSeconds = Math.round(this.gaVODPlaybackTime / 1000);

    if (
      checkHeartBeat(
        this.gaVODPlaybackTime,
        GA_VOD_HEART_BEAT_FIRE_INTERVAL,
        timeInSeconds,
        this.lastGAVodEventTime,
      )
    ) {
      gaHeartBeat();
      this.lastGAVodEventTime = timeInSeconds;
    }
  };

  onAdStart = () => {
    this.props.gaAdImpression();
  };

  getStartPosition = () => {
    const { playbackPosition: resumePlayback } = this.props;
    if (resumePlayback.kind === constants.player.PLAYER_CONTENT_KIND.VOD) {
      const sameItem = resumePlayback.itemId === this.props.itemId;

      if (sameItem) {
        const { position, duration } = resumePlayback;

        if (position < duration) return position;
      }
    }

    return -1;
  };

  // eslint-disable-next-line react/sort-comp
  initYouboraPlugin = () => {
    if (typeof document !== 'undefined') {
      // Don't use youbora plugin for SSR
      return new youbora.Plugin({
        accountCode: process.env.NODE_ENV === 'production' ? 'plutotv' : 'plutotvdev',
      });
    }
    return null;
  };

  initYouboraAdapter = (player) => {
    try {
      const adapter = new YouboraClapprPlayerAdapter(player);
      this.youboraPlugin.setAdapter(adapter);
    } catch (err) {
      logger('youboraPlugin fails with: ', err);
    }
  };

  player;
  youboraPlugin;
  destroying = false;
  videoRef = createRef();
  gaVODPlaybackTime = 0; // in ms
  lastGAVodEventTime = 0; // in s

  isMuted = () => {
    if (this.state.firstPlay) {
      return true;
    }

    return this.props.isMuted;
  };

  initialize = async () => {
    if (!this.videoRef.current) {
      return;
    }
    const { url, googPalNonce } = this.props;
    logger('initializing', url);

    this.tryDestroy();

    global.Clappr = await import('./../../../../clappr').then((module) => module.default);

    const player = new global.Clappr.Player({
      parent: this.videoRef.current,
      source: `${url}${googPalNonce}`,
      autoPlay: false,
      persistConfig: false,
      chromeless: true,
      width: '100%',
      height: '100%',
      muted: false,
      playback: {
        playInline: true,
        hlsjsConfig: {
          ...HLS_CONFIG,
          startPosition: this.getStartPosition(),
          debug: localStorage.hlsDebug || false,
        },
      },
    });

    if (this.youboraPlugin) {
      const options = {
        'parse.hls': true,
        'parse.cdnNodeHost': true,
      };
      this.youboraPlugin.setOptions(options);
      this.initYouboraAdapter(player);
    }

    const originalPause = player.pause.bind(player);
    const originalPlay = player.play.bind(player);

    player.pause = () => {
      if (this.props.isVod) {
        this.setState({ paused: true });
      }
      originalPause();
    };

    player.play = () => {
      if (this.props.isVod) {
        this.setState({ paused: false });
      }
      originalPlay();
    };

    // Using once(PLAYER_PLAY) until find why PLAYBACK_READY is not working
    player.once(global.Clappr.Events.PLAYER_PLAY, this.handleVideoReady);
    player.on(global.Clappr.Events.PLAYER_PLAY, this.handleVideoPlaying);
    player.on(global.Clappr.Events.PLAYER_PAUSE, this.handleVideoPause);
    player.on(global.Clappr.Events.PLAYER_ENDED, this.handleVideoEnded);
    player.on(global.Clappr.Events.PLAYER_TIMEUPDATE, this.handleVideoTimeUpdate);
    player.on(global.Clappr.Events.PLAYER_ERROR, this.handleVideoError);
    player.on(global.Clappr.Events.PLAYER_RESIZE, this.handleVideoResize);
    player.listenTo(
      player.core.activeContainer.playback,
      global.Clappr.Events.PLAYBACK_BUFFERING,
      this.handleVideoWaiting,
    );

    this.clearAutoplay();

    this.setState(
      {
        player,
        ready: false,
        readyCheck: 0,
        autoplayBlocked: false,
      },
      () => {
        this.tryAutoplay();
      },
    );
  };

  checkReadyTimer;

  checkReady = () => {
    if (this.state.ready) {
      logger('Player ready');
      this.retryOnErrorCount = 0;
      return;
    }

    if (this.checkReadyTimer) {
      clearTimeout(this.checkReadyTimer);
    }

    if (this.state.readyCheck < 25) {
      this.setState({ readyCheck: this.state.readyCheck + 1 });
      this.checkReadyTimer = setTimeout(this.checkReady, 200);
    } else {
      logger('Not ready after a while');

      this.clearAutoplay();
      this.tryAutoplay();
    }
  };

  clearAutoplay = () => {
    this.setState({
      tryMuted: false,
      tryPlayCount: 0,
    });
  };

  tryAutoplay = () => {
    if (!this.state.player) {
      // This should never happen, but if happen, retry after timeout
      setTimeout(() => {
        this.tryAutoplay();
      }, 100);
      return;
    }
    const { player } = this.state;
    logger('checkAutoplay', { isPlaying: player.isPlaying() });
    if (!player.isPlaying()) {
      if (this.state.tryPlayCount === 0) {
        logger('Auto play trying to play for the first time');
        if (this.isMuted()) {
          player.mute();
          this.props.mutePlayer();
        }
        player.play();
        this.setState({ tryPlayCount: 1 });
      } else if (!this.state.tryMuted) {
        logger('Auto play failed, muting and retrying');
        player.mute();
        this.props.mutePlayer();
        this.setState({ tryMuted: true, tryPlayCount: 2 });
        player.play();
      } else {
        logger("Can't autoplay");
        this.props.onAutoPlayBlocked(player, global.Clappr.Events);
        this.setState({ autoplayBlocked: true });
        player.unmute();
        this.props.unmutePlayer();
        return;
      }
      setTimeout(this.tryAutoplay, 50);
    } else {
      this.setState({ tryMuted: false, tryPlayCount: 0, readyCheck: 0 });
      this.checkReady();
    }
  };

  checkPlayingTimer;
  startCheckPlaying = () => {
    if (this.checkPlayingTimer) {
      clearInterval(this.checkPlayingTimer);
    }
    logger('Initializing check playing interval');

    this.setState({ checkPlayingCount: 0 });
    this.checkPlayingTimer = setInterval(this.checkPlaying, 1000);
  };

  checkPlaying = () => {
    const { player, checkPlayingCount, autoplayBlocked, paused } = this.state;
    if (autoplayBlocked || paused || !player) {
      return;
    }

    if (player.isPlaying()) {
      this.setState({ checkPlayingCount: 0 });
      return;
    }
    logger('Player not is playing');

    logger(`Play check count: ${checkPlayingCount}`);
    if (checkPlayingCount > 20) {
      logger('Cannot play, reinitializing player');
      this.initialize();
      return;
    }

    player.play();
    this.setState({ checkPlayingCount: checkPlayingCount + 1 });
  };

  tryDestroy = () => {
    if (this.state.player) {
      this.destroying = true;
      this.state.player.destroy();
      this.state.player = undefined;
      if (this.props.onDestroy) {
        this.props.onDestroy();
      }
    }
  };

  setCaptions = (e) => {
    const tracks = this.state.player.core.activeContainer.playback.closedCaptionsTracks;
    logger('tracks found: %O, event: %o', tracks, e);
    tracks.forEach(({ track }) => {
      track.mode = 'hidden';
    });
    this.props.onCaptionList(tracks);
  };

  configureCaptions = () => {
    const htmlPlayerElement = this.state.player.core.activeContainer.playback.$el[0];
    if (htmlPlayerElement && htmlPlayerElement.textTracks) {
      logger('configuring captions');
      htmlPlayerElement.textTracks.addEventListener('addtrack', this.setCaptions);
      htmlPlayerElement.textTracks.addEventListener('removetrack', this.setCaptions);
      this.setCaptions();
    }
  };

  handleVideoResize = () => {
    logger('Video Resizeing');
    const videoElement = global.window.document.getElementsByTagName('video');
    this.props.savePlayerSizes({
      videoHeight: videoElement[0].videoHeight,
      videoWidth: videoElement[0].videoWidth,
      playerHeight: videoElement[0].clientHeight,
      playerWidth: videoElement[0].clientWidth,
    });
  };

  handleVideoReady = () => {
    logger('Video Ready');
    this.configureCaptions();
    const { player } = this.state;
    this.props.onReady(player, global.Clappr.Events);
    this.setState({
      ready: true,
      firstPlay: false,
    });
    this.destroying = false;

    /* eslint-disable no-underscore-dangle */
    if (player.core.activeContainer.playback._hls) {
      player.core.activeContainer.playback._hls.on(
        global.Clappr.HLS.HLSJS.Events.DESTROYING,
        this.handleDestroying,
      );

      // Get the id of just the first fragment as early as possible, before it's loaded,
      // parsed, decrypted, etc, to ensure minimal lag on any ad overlay once render starts.
      // This is to address the case when the initial playback starts in the middle of an ad,
      // and we want any corresponding interactive elements to show in sync ASAP.
      player.core.activeContainer.playback._hls.once(
        global.Clappr.HLS.HLSJS.Events.FRAG_LOADING,
        this.handleHLSFragmentEvent,
      );

      // Update adId on each fragment change.
      player.core.activeContainer.playback._hls.on(
        global.Clappr.HLS.HLSJS.Events.FRAG_CHANGED,
        this.handleHLSFragmentEvent,
      );
    }
    /* eslint-enable no-underscore-dangle */

    this.startCheckPlaying();
  };

  handleHLSFragmentEvent = (event, data) => {
    // Reset the initial load listener again, over and over, until we get the first `.ts` file.
    // For example, we want to ignore loading events of `.vtt` files.
    if (
      event === global.Clappr.HLS.HLSJS.Events.FRAG_LOADING &&
      !data.frag.relurl.endsWith('.ts')
    ) {
      const { player } = this.state;
      /* eslint-disable no-underscore-dangle */
      player.core.activeContainer.playback._hls.once(
        global.Clappr.HLS.HLSJS.Events.FRAG_LOADING,
        this.handleHLSFragmentEvent,
      );
      return;
      /* eslint-enable no-underscore-dangle */
    }

    // A content fragment will have a url structured like:
    // "http://siloh-aka.plutotv.net/d71341_Pluto_TV_OandO/clip/5baf2067ea5c9e53afd1f90b_September_30th_2018/720p/20200303_135941/hls/540001-570000/hls_2400-00000.ts"
    // An ad fragment will have a url structured like:
    // "http://siloh-aka.plutotv.net/f1cb9d_29_ad/creative/5e5b6b843050a80007d57385_ad/720p/20200301_000004/hls/hls_2400-00005.ts"
    // which enables us to derive the ad id from the URL.
    let currentlyPlayingAdIdAccordingToStream = '';
    const parsedRelUrl = parse(data.frag.relurl);
    const thirdFragment = parsedRelUrl.pathname.split('/')[3];
    if (thirdFragment && thirdFragment.endsWith('_ad')) {
      currentlyPlayingAdIdAccordingToStream = thirdFragment.slice(0, -3);
    }
    this.props.setCurrentlyPlayingAdIdAccordingToStream(currentlyPlayingAdIdAccordingToStream);
  };

  handleDestroying = () => {
    if (!this.destroying) {
      logger('Destroyed, reinitializing');
      this.initialize();
    }
  };

  handleVideoPlaying = () => {
    logger('Video Playing');
    const videoElement = global.window.document.getElementsByTagName('video');
    this.props.savePlayerSizes({
      videoHeight: videoElement[0].videoHeight,
      videoWidth: videoElement[0].videoWidth,
      playerHeight: videoElement[0].clientHeight,
      playerWidth: videoElement[0].clientWidth,
    });
    this.props.onPlay();
  };

  handleVideoPause = () => {
    const { player } = this.state;
    logger('Video Paused');
    if (player && player.buffering) {
      this.props.onBuffer();
    } else {
      this.props.onPause();
    }
  };

  handleVideoWaiting = () => {
    this.props.onBuffer();
  };

  handleVideoEnded = () => {
    if (this.props.isVod) {
      this.tryDestroy();
      this.props.onComplete();
    } else if (this.props.isViral) {
      this.tryDestroy();
    } else {
      // This is here because services insert #EXT-X-ENDLIST
      // which is a false ending. We need to skip over it and
      // continue to play the same stream.
      this.initialize();
    }
  };

  handleVideoTimeUpdate = throttle(
    (e) => {
      const { kind, onTime, itemId, savePlaybackPosition } = this.props;

      savePlaybackPosition({
        itemId,
        kind,
        position: Math.floor(e.current),
        duration: e.total,
      });
      onTime(e);
    },
    500,
    { trailing: true },
  );

  retryWait = 5000;
  retryOnErrorCount = 0;
  throttledRetry = throttle(() => {
    console.warn('retrying now');
    this.initialize();
    this.retryOnErrorCount += 1;
  }, this.retryWait);
  handleVideoError = () => {
    if (this.retryOnErrorCount <= 10) {
      console.warn('video error, will retry soon');
      this.throttledRetry();
    } else {
      console.error('Giving up to retry on error');
    }
  };

  render() {
    return (
      <Fragment>
        <div style={{ width: '100%', height: '100%' }} ref={this.videoRef} />
        {this.state.player && (
          <Fragment>
            <Captions />
            <components.PodTrackerAds
              key="coreAdsTracker"
              playerInstance={this.state.player}
              onAdStart={this.onAdStart}
              playerEventsList={global.Clappr.Events}
            />
            <components.Player
              key="corePlayer"
              playerInstance={this.state.player}
              onHeartBeat={this.onHeartBeat}
              playerEventsList={global.Clappr.Events}
            />
          </Fragment>
        )}
      </Fragment>
    );
  }
}

export { ClapprVideoPlayer };
